import { occupancy } from './board';
import { moveMods, placePillars } from './ledger/hooks';
import type { BoardCtx } from './movement';
import type { ChainState, Face, MatchState, PlayerState, Side } from './types';

/** Copies every mutable container of a state. Value objects (pawns, cards, epitaphs…) are shared. */
export function cloneState(s: MatchState): MatchState {
  return {
    ...s,
    pawns: { ...s.pawns },
    players: { S: clonePlayer(s.players.S), N: clonePlayer(s.players.N) },
    pile: s.pile.slice(),
    chain: s.chain ? cloneChain(s.chain) : null,
    rounds: s.rounds.slice(),
  };
}

function clonePlayer(p: PlayerState): PlayerState {
  return {
    ...p,
    deck: p.deck.slice(),
    hand: p.hand.slice(),
    spent: p.spent.slice(),
    removed: p.removed.slice(),
    epitaphs: p.epitaphs.slice(),
    pendingCrownBonuses: p.pendingCrownBonuses.slice(),
    reserves: p.reserves.slice(),
    reservesPawnedThisRound: p.reservesPawnedThisRound.slice(),
    collateralUsed: p.collateralUsed.slice(),
  };
}

function cloneChain(c: ChainState): ChainState {
  return {
    ...c,
    links: c.links.slice(),
    cardIds: c.cardIds.slice(),
    landings: c.landings.slice(),
    visited: c.visited.slice(),
    ...(c.ledgerLinks ? { ledgerLinks: c.ledgerLinks.slice() } : {}),
  };
}

/** The top of the pile (§7.2), or null when the pile is empty. */
export const topFace = (s: Pick<MatchState, 'pile'>): Face | null =>
  s.pile[s.pile.length - 1]?.face ?? null;

export function boardCtx(s: Pick<MatchState, 'pawns' | 'config' | 'ledger'>): BoardCtx {
  if (!s.ledger)
    return { occ: occupancy(s.pawns), pawns: s.pawns, immunity: s.config.homeRowImmunity };
  const mods = moveMods(s);
  return {
    occ: placePillars(s, occupancy(s.pawns)),
    pawns: s.pawns,
    immunity: s.config.homeRowImmunity,
    ...(mods ? { mods } : {}),
  };
}

export const squareKey = (sq: { x: number; y: number }): string => `${sq.x},${sq.y}`;

export const player = (s: MatchState, side: Side): PlayerState => s.players[side];
